class DeliveryCutoffTimer extends HTMLElement {
  connectedCallback() {
      const container = this;

      const workingDays = container.dataset.workingDays
         .split(',')
         .map(s => s.trim())
         .filter(Boolean)
         .map(Number);

      const cutoffHour = parseInt(container.dataset.cutoffHour, 10);
      const cutoffMinute = parseInt(container.dataset.cutoffMinute, 10);
      const shippingDays = parseInt(container.dataset.shippingDays, 10);
      const shippingDayMax = parseInt(container.dataset.shippingDayMax, 10);
      const template = container.dataset.messageTemplate;
      const storeTimeZone = container.dataset.storeTimezone || 'Europe/London';

      // Parse holiday dates (expected format: dd/mm/yyyy, one per line)
      const holidayDates = (container.dataset.holidayDates || '')
         .split('\n')
         .map(s => s.trim())
         .filter(Boolean)
         .map(dateStr => {
             const parts = dateStr.split('/');
             if (parts.length === 3) {
                 const day = parseInt(parts[0], 10);
                 const month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
                 const year = parseInt(parts[2], 10);
                 return new Date(year, month, day);
             }
             return null;
         })
         .filter(date => date !== null);

      const outputEl = container.querySelector('[data-cutoff-output]');
      if (!outputEl) return;

      function getStoreNow() {
          const now = new Date();
          const formatter = new Intl.DateTimeFormat('en-GB', {
              timeZone: storeTimeZone,
              hour12: false,
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
          });

          const parts = formatter.formatToParts(now).reduce((acc, part) => {
              if (part.type !== 'literal') acc[part.type] = part.value;
              return acc;
          }, {});

          return new Date(`${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`);
      }

      function isHoliday(date) {
          return holidayDates.some(holiday => {
              return holiday.getDate() === date.getDate() &&
                     holiday.getMonth() === date.getMonth() &&
                     holiday.getFullYear() === date.getFullYear();
          });
      }

      function isWorkingDay(date) {
          return workingDays.includes(date.getDay()) && !isHoliday(date);
      }

      function getNextWorkingDay(date) {
          const d = new Date(date);
          do {
              d.setDate(d.getDate() + 1);
          } while (!isWorkingDay(d));
          return d;
      }

      function addWorkingDays(date, days) {
          let d = new Date(date);
          let added = 0;
          while (added < days) {
              d.setDate(d.getDate() + 1);
              if (isWorkingDay(d)) {
                  added++;
              }
          }
          return d;
      }

      function getOrdinalSuffix(day) {
          if (day > 3 && day < 21) return 'th'; // catch 11–13
          switch (day % 10) {
              case 1:  return 'st';
              case 2:  return 'nd';
              case 3:  return 'rd';
              default: return 'th';
          }
      }

      function updateTimer() {
          const now = getStoreNow();
          let dispatchDate = new Date(now);
          let cutoffTimeToday = new Date(dispatchDate);
          cutoffTimeToday.setHours(cutoffHour, cutoffMinute, 0, 0);

          const isTodayWorkingDay = isWorkingDay(now);
          if (!isTodayWorkingDay || now > cutoffTimeToday) {
              dispatchDate = getNextWorkingDay(dispatchDate);
          }

          const deliveryDate = addWorkingDays(dispatchDate, shippingDays);

          let finalDispatchDate = new Date(deliveryDate);
          let shippingCountdown = shippingDays;
          while (shippingCountdown > 0) {
              finalDispatchDate.setDate(finalDispatchDate.getDate() - 1);
              if (isWorkingDay(finalDispatchDate)) {
                  shippingCountdown--;
              }
          }

          const cutoffDeadline = new Date(finalDispatchDate);
          cutoffDeadline.setHours(cutoffHour, cutoffMinute, 0, 0);

          const timeDiffMs = cutoffDeadline - now;
          const secondsLeft = Math.max(0, Math.floor(timeDiffMs / 1000));
          const hours = Math.floor(secondsLeft / 3600);
          const minutes = Math.floor((secondsLeft % 3600) / 60);
          const seconds = secondsLeft % 60;

          const hoursFormatted = String(hours).padStart(2, '0');
          const minutesFormatted = String(minutes).padStart(2, '0');
          const secondsFormatted = String(seconds).padStart(2, '0');

          // Build time string, only show hours if > 0
          let timeComponents = [];
          if (hours > 0) {
              timeComponents.push(`${hoursFormatted} Hr${hours !== 1 ? 's' : ''}`);
          }
          timeComponents.push(`${minutesFormatted} Min${minutes !== 1 ? 's' : ''}`);
          timeComponents.push(`${secondsFormatted} Sec${seconds !== 1 ? 's' : ''}`);

          const timeLeftFormatted = `<span class="cutoff-timer__time-left">${timeComponents.join(', ')}</span>`;

          // Format delivery date(s)
          let deliveryFormatted;
          if (shippingDayMax && shippingDayMax > shippingDays) {
              // Show date range
              const maxDeliveryDate = addWorkingDays(dispatchDate, shippingDayMax);
              
              const minMonth = deliveryDate.toLocaleDateString('en-GB', { month: 'short' });
              const minDay = String(deliveryDate.getDate()).padStart(2, '0');
              const maxMonth = maxDeliveryDate.toLocaleDateString('en-GB', { month: 'short' });
              const maxDay = String(maxDeliveryDate.getDate()).padStart(2, '0');
              
              deliveryFormatted = `<span class="cutoff-timer__delivery-date">${minMonth} ${minDay} - ${maxMonth} ${maxDay}</span>`;
          } else {
              // Show single date
              const month = deliveryDate.toLocaleDateString('en-GB', { month: 'short' });
              const day = String(deliveryDate.getDate()).padStart(2, '0');
              
              deliveryFormatted = `<span class="cutoff-timer__delivery-date">${month} ${day}</span>`;
          }

          const finalMessage = template
             .replace('[[time_left]]', timeLeftFormatted)
             .replace('[[delivery_day]]', deliveryFormatted);

          outputEl.innerHTML = finalMessage;
      }

      // Initial update
      updateTimer();

      // Update every second
      this.intervalId = setInterval(updateTimer, 1000);
  }

  disconnectedCallback() {
      // Clean up interval when component is removed
      if (this.intervalId) {
          clearInterval(this.intervalId);
      }
  }
}

customElements.define('delivery-cutoff-timer', DeliveryCutoffTimer);